// Brand faces, self-hosted via @fontsource — no external font host. These must
// be imported from JS, not from index.css: Tailwind v4 flattens CSS @imports
// before Vite can rewrite the `url()` refs, so the woff2 files never reach
// dist/. Same set and same reason as studio/client/src/main.tsx.
//
// They were MISSING when the hymnal first shipped, alongside the missing token
// import — and fixing the tokens alone was not enough. The tokens NAME the
// families (Fraunces for display, Space Grotesk for text) but load no
// @font-face, so all 968 pages went on rendering in system fallbacks: right
// colours, wrong letters, and nothing in the build says a word about it.
import '@fontsource-variable/fraunces'
import '@fontsource/space-grotesk/400.css'
import '@fontsource/space-grotesk/500.css'
import '@fontsource/space-grotesk/700.css'
import '@fontsource/ibm-plex-mono/400.css'
import '@fontsource/ibm-plex-mono/500.css'
import './index.css'
import App from './App.tsx'
import { mountClient } from '@gjl/site-kit'
import { initGA4 } from '@gjl/analytics'

/**
 * GA4 — the SAME property as fuguelab.com, deliberately.
 *
 * This is one codebase, one domain, one bucket, one CloudFront distribution and one
 * pipeline; the split into a second Vite build exists only to keep the 341.8KB hymnal
 * dataset out of the marketing chunk (D9). GA4 tracks a data stream for a DOMAIN, so
 * `/hymnal/*` belongs in the property that already covers `/`.
 *
 * A separate property would be actively wrong: a visitor moving from a hymn page to
 * the Canticle page is one session on one domain, and splitting it would break the
 * hymnal → Canticle funnel this project exists to measure. Segment by path instead.
 *
 * Via `initGA4` rather than an inline gtag snippet: `scripts/ci/ga4-guard.mjs` keeps a
 * grandfathered LEGACY_FORKS list — `studio/client/index.html` is on it — and says
 * plainly "Do NOT add entries to keep a new fork: the fix for a new site is
 * initGA4()". Copying studio's snippet would have added the seventh fork.
 *
 * Dev stays untracked: `initGA4` no-ops on a falsy id.
 *
 * THE ID IS BUILD CONFIG, NOT A LITERAL (2026-09-05). This used to fall back to
 * a hardcoded `G-0WNEJ3Q8E4` whenever `import.meta.env.PROD` was true — but PROD
 * is true for EVERY production-mode build, including the dev-environment deploy
 * and the prerender CI runs on a PR. So dev.fuguelab.com and CI both reported
 * into the production property: 19 and 21 sessions respectively over 60 days,
 * mixed into 1,139 real ones.
 *
 * Now the id comes only from `VITE_GA_ID`, which the buildspec exports for the
 * prod environment alone. A dev build has no id and `initGA4` no-ops.
 *
 * THE RISK THIS CREATES, AND WHERE IT IS HANDLED: a missing var no longer falls
 * back to something that works, it falls back to silence — the exact shape of
 * the recipes VITE_API_URL incident. The prod buildspec asserts the var is set
 * before building, so prod fails loudly rather than going dark quietly.
 */
const GA_ID = import.meta.env.VITE_GA_ID as string | undefined
initGA4(GA_ID)

// Prerendered HTML -> hydrate; empty root (dev) -> fresh render.
mountClient(App)
